import { fromInteger } from '@repo/money'
import { useEffect, useMemo, useState } from 'react'
import { useDailyCheckInActions } from 'src/features/dailyCheckIn'
import { useInventoryActions } from 'src/features/inventory/store'
import { useNotificationActions } from 'src/features/notification/store'
import { useClientSideTranslation } from 'src/services/i18n'
import { useBetActions } from 'src/store/bet'
import { useGameActions, useGetCurrentGameId, useGetCurrentLanguage } from 'src/store/game'
import {
  useGetCurrentPlayer,
  useGetCurrentPlayerId,
  useGetCurrentPlayerToken,
  useIsBalanceLoaded,
  usePlayerActions
} from 'src/store/player'
import { useSocialActions } from 'src/store/social'
import { useSocialStreamActions } from 'src/store/socialStream'
import { useGetPlayerChannel, useGetSocket, useSocketActions } from 'src/store/socket'
import { useUIActions } from 'src/store/ui'
import { isInitializedPlayer, type AuthenticatedPlayer } from 'src/types/domain/Player'
import { setupChannelListeners } from 'src/utils/setupChannelListeners'
import {
  handleBetAccepted,
  handleBetCashedOut,
  handleBetRejected,
  handleBetVoided,
  handleBetWon,
  handleInventoryItemAdded,
  handleInventoryItemUpdated,
  handlePlayerLevelUpdated,
  handlePlayerStatsUpdated,
  handleStreaks,
  handleStreakUpdated,
  handleStreamPaused,
  handleStreamWarning,
  type Actions
} from 'src/utils/websocketEventHandlers'
import { usePlayerLevelActions } from 'src/store/playerLevel'
import { emitGameLoaded } from 'src/services/postMessage'
import { usePlayerBalance } from './usePlayerBalance'

export const usePlayerListeners = () => {
  const socketActions = useSocketActions()
  const socialStreamActions = useSocialStreamActions()
  const socket = useGetSocket()
  const playerChannel = useGetPlayerChannel()
  const playerId = useGetCurrentPlayerId()
  const currentPlayer = useGetCurrentPlayer()
  const currentGameId = useGetCurrentGameId()
  const betActions = useBetActions()
  const gameActions = useGameActions()
  const socialActions = useSocialActions()
  const playerActions = usePlayerActions()
  const uiActions = useUIActions()
  const dailyCheckinActions = useDailyCheckInActions()
  const token = useGetCurrentPlayerToken()
  const { updateBalance } = usePlayerBalance()
  const inventoryActions = useInventoryActions()
  const notificationActions = useNotificationActions()
  const playerLevelActions = usePlayerLevelActions()
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)

  const hasSocket = Boolean(socket)
  const isPlayerChannelJoined = playerChannel?.state === 'joined'
  const isBalanceLoaded = useIsBalanceLoaded()

  // Latched when the server pushes `session:expired` (its balance calls to the
  // operator keep failing for this session). Gates the balance retry loop —
  // a dead session must never turn into an infinite retry/reconnect hammer.
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  // QA affordance: artificially delay the post-join balance fetch so the loading
  // UX (skeleton + "Initializing balance" modal + disabled betting) is observable
  // on fast / funny-money environments where the balance otherwise lands instantly.
  // Reads `localStorage.debugBalanceDelayMs` FIRST (persists across the one-time-OTL
  // launch + the root→game redirect that can drop query params), falling back to a
  // `?debugBalanceDelayMs=<n>` URL param. Off (0) unless set; capped 30s.
  // Set:   localStorage.setItem('debugBalanceDelayMs','4000')
  // Clear: localStorage.removeItem('debugBalanceDelayMs')
  const debugBalanceDelayMs = useMemo(() => {
    if (typeof window === 'undefined') return 0
    let fromStorage: string | null = null
    try {
      fromStorage = window.localStorage.getItem('debugBalanceDelayMs')
    } catch {
      fromStorage = null
    }
    const fromUrl = new URLSearchParams(window.location.search).get('debugBalanceDelayMs')
    const raw = fromStorage ?? fromUrl
    const n = raw ? parseInt(raw, 10) : 0
    return Number.isFinite(n) && n > 0 ? Math.min(n, 30_000) : 0
  }, [])

  const actions: Actions = useMemo(
    () => ({
      bet: betActions,
      player: playerActions,
      social: socialActions,
      ui: uiActions,
      game: gameActions,
      socket: socketActions,
      socialStream: socialStreamActions,
      inventory: inventoryActions,
      notification: notificationActions,
      dailyCheckin: dailyCheckinActions,
      playerLevel: playerLevelActions
    }),
    [
      betActions,
      playerActions,
      socialActions,
      uiActions,
      gameActions,
      socketActions,
      socialStreamActions,
      inventoryActions,
      notificationActions,
      dailyCheckinActions,
      playerLevelActions
    ]
  )

  // Join Player channel
  useEffect(() => {
    if (!hasSocket || isPlayerChannelJoined) return

    if (!isInitializedPlayer(currentPlayer)) return

    void actions.socket.joinPlayerChannel(currentPlayer.id, (response) => {
      if (!token) return

      // Non-blocking launch: the server may defer the balance amount (it omits it
      // until the operator wallet responds). Currency is always present. When the
      // amount is absent we render with a placeholder and `balanceLoaded: false`,
      // then fill it via the post-join balance fetch below. A reply WITH the
      // amount (current server / demo) loads it immediately — forward-compatible.
      const rawBalanceAmount = response.player.balance.amount
      const balanceLoaded = typeof rawBalanceAmount === 'number'
      const playerBalance = {
        amount: balanceLoaded ? fromInteger(rawBalanceAmount, response.player.balance.currency) : 0,
        currency: response.player.balance.currency
      }

      const player: AuthenticatedPlayer = {
        type: 'AuthenticatedPlayer',
        id: response.player.id,
        token,
        sessionId: response.player.sessionId,
        username: response.player.username,
        balance: playerBalance,
        balanceLoaded,
        displayName: response.player.displayName,
        language: response.player.language,
        country: response.player.country
      }

      const allowedGameIds = response.games.map((game: { id: string }) => game.id)

      const gameConfig = {
        lobbyDisabled: response.gameConfig.lobbyDisabled,
        externalLobbyUrl: response.gameConfig.externalLobbyUrl,
        externalDepositUrl: response.gameConfig.externalDepositUrl,
        platform: response.player.platform,
        language: response.player.language,
        country: response.player.country
      }

      actions.player.authenticated(player)
      actions.game.tokenGameConfigRetrieved(gameConfig)
      actions.game.setAllowedGameIds(allowedGameIds)

      // Emit game:loaded to parent iframe
      emitGameLoaded({
        gameId: currentGameId ?? '',
        gameType: '', // Will be populated once game data is available
        playerId: player.id
      })

      // Set inventory items from response
      if (response.inventory.length > 0) {
        inventoryActions.setInventoryItems(response.inventory)
      }

      // Set player stats from response (for level system)
      if (response.playerStats) {
        const stats = response.playerStats as {
          level: number
          xp: number // XP in cents (displayed as unitless XP)
          multiplierBonus: number
          totalBets: number
          totalWins: number
          totalLosses: number
          winRate: number
          // Currency-converted values (optional, may be missing if session not found)
          totalWagered?: number // In player's currency minor units
          totalWon?: number // Total amount won in player's currency minor units
          biggestWin?: number // In player's currency minor units
          currency?: string // Player's session currency code
        }

        playerLevelActions.setLevelSystemEnabled(true)
        playerLevelActions.setPlayerLevel({
          level: stats.level,
          xp: stats.xp, // Keep as cents, displayed as unitless XP
          multiplierBonus: stats.multiplierBonus
        })
        playerLevelActions.setPlayerStats({
          totalBets: stats.totalBets,
          totalWins: stats.totalWins,
          winRate: stats.winRate,
          totalWagered: stats.totalWagered,
          totalWon: stats.totalWon,
          biggestWin: stats.biggestWin,
          currency: stats.currency
        })
      }
    })
  }, [
    actions.game,
    actions.player,
    actions.socket,
    currentPlayer,
    hasSocket,
    isPlayerChannelJoined,
    token,
    inventoryActions,
    playerLevelActions
  ])

  // Non-blocking launch: if the server deferred the balance (so the game could
  // render without waiting on the operator wallet), fetch it now over the same
  // proven `client:player:balance:get` path the bet flow already uses. Retry
  // until it lands — the game is up but betting stays hard-blocked until then.
  // When `balanceChanged` flips `balanceLoaded` true, this effect re-runs, hits
  // the early return, and the cleanup clears the interval.
  //
  // BOUNDED (leak fix): this loop used to retry every 2.5s FOREVER. A session
  // the operator no longer recognizes never loads a balance, so a stale tab
  // hammered the socket-server → internal-game-service → operator wallet
  // indefinitely (the observed reconnect/balance loop feeding the socket
  // memory leak). Now: 2.5s for the first ~25s, then 30s, and after
  // ~5 minutes of continuous failure it stops for good. The server
  // independently pushes `session:expired` after its own failure threshold
  // (handled below), which stops the loop immediately.
  useEffect(() => {
    if (!isPlayerChannelJoined || isBalanceLoaded || isSessionExpired) return

    const FAST_MS = 2_500
    const SLOW_MS = 30_000
    const FAST_ATTEMPTS = 10 // ~25s of fast retries
    const MAX_ATTEMPTS = 20 // then 10 slow ones ≈ 5 minutes total

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = () => {
      if (cancelled) return
      attempts += 1
      void updateBalance()

      if (attempts >= MAX_ATTEMPTS) {
        console.error(
          'Balance still unavailable after prolonged retries — stopping. Reload to retry.'
        )
        return
      }
      timer = setTimeout(tick, attempts < FAST_ATTEMPTS ? FAST_MS : SLOW_MS)
    }

    // Normally starts immediately (delay 0); the QA param delays the first fetch
    // so the loading UX is observable.
    const startTimer = setTimeout(tick, debugBalanceDelayMs)

    return () => {
      cancelled = true
      clearTimeout(startTimer)
      if (timer) clearTimeout(timer)
    }
  }, [isPlayerChannelJoined, isBalanceLoaded, isSessionExpired, updateBalance, debugBalanceDelayMs])

  // Server-declared stale session: the socket-server pushes `session:expired`
  // after its own consecutive-balance-failure threshold and stops the channel.
  // Stop ALL retrying immediately — reconnect/rejoin cannot fix a session the
  // operator no longer recognizes; only a fresh game launch (new token) can.
  useEffect(() => {
    if (!isPlayerChannelJoined) return

    const cleanup = setupChannelListeners(playerChannel, [
      {
        event: 'session:expired',
        handler: () => {
          console.error(
            'Session expired (server signal) — stopping balance retries; a fresh game launch is required.'
          )
          setIsSessionExpired(true)
        }
      }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel])

  // Attach Player channel bet event listeners
  useEffect(() => {
    if (!isPlayerChannelJoined || !playerId) return

    const cleanup = setupChannelListeners(playerChannel, [
      { event: 'bet:accepted', handler: handleBetAccepted(actions, updateBalance) },
      { event: 'bet:rejected', handler: handleBetRejected(actions, updateBalance) },
      { event: 'bet:won', handler: handleBetWon(actions, updateBalance) },
      { event: 'bet:voided', handler: handleBetVoided(actions, updateBalance) },
      { event: 'bet:cashed-out', handler: handleBetCashedOut(actions, updateBalance) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel, actions, updateBalance, playerId])

  // Attach Player channel stream event listeners
  useEffect(() => {
    if (!isPlayerChannelJoined || !playerId || !currentGameId) return

    const cleanup = setupChannelListeners(playerChannel, [
      { event: 'stream:warning', handler: handleStreamWarning(actions, playerId, currentGameId) },
      { event: 'stream:pause', handler: handleStreamPaused(actions, playerId, currentGameId) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel, actions, playerId, currentGameId])

  // Attach Player channel streaks event listeners
  useEffect(() => {
    if (!isPlayerChannelJoined || !playerId) return

    const cleanup = setupChannelListeners(playerChannel, [
      { event: 'streaks', handler: handleStreaks(actions) },
      { event: 'streak:updated', handler: handleStreakUpdated(actions) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel, playerId, actions])

  // Attach Player channel inventory event listeners
  useEffect(() => {
    if (!isPlayerChannelJoined || !playerId) return
    const cleanup = setupChannelListeners(playerChannel, [
      { event: 'inventory-item:added', handler: handleInventoryItemAdded(actions, t) },
      { event: 'inventory-item:updated', handler: handleInventoryItemUpdated(actions) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel, playerId, actions, t])

  // Attach Player channel level event listeners
  useEffect(() => {
    if (!isPlayerChannelJoined || !playerId) return
    const cleanup = setupChannelListeners(playerChannel, [
      { event: 'player_level_updated', handler: handlePlayerLevelUpdated(actions, t) },
      { event: 'player_stats_updated', handler: handlePlayerStatsUpdated(actions, t) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isPlayerChannelJoined, playerChannel, playerId, actions, t])
}
